/**
 * Button class recipe — composes size + color ingredients.
 *
 * Shape tokens: Button.size.ts. Ink tokens: Button.color.ts.
 * Dashed capability: `DASHED_TYPES` in Button.types.ts (type + runtime).
 */

import { DASHED_TYPES, type ButtonIntent, type ButtonSize, type ButtonType } from './Button.types';
import {
  BUTTON_STROKE,
  BUTTON_STROKE_DASHED,
  INTERACTION_BASE,
  INTENT_COLORS,
  SELECTED_SURFACE,
  STATE_LAYER,
  buttonDashInk,
} from './Button.color';
import {
  HIT_TARGET_MIN,
  PADDING,
  PADDING_ICON,
  PADDING_WITH_TRAILING_PLATE,
  ROUNDED,
  SIZE_STYLES,
  SIZE_STYLES_WITH_TRAILING_PLATE,
} from './Button.size';

/** Runtime mirror of the compile-time dashed constraint — same `DASHED_TYPES`. */
const canDash = (type: ButtonType): boolean =>
  (DASHED_TYPES as readonly ButtonType[]).includes(type);

/** Compose production Button className. `isSelected` → SELECTED_SURFACE. */
export const getButtonClasses = (
  type: ButtonType,
  intent: ButtonIntent | string,
  size: ButtonSize,
  isDisabled: boolean,
  className: string = '',
  isSelected: boolean = false,
  isDashed: boolean = false,
  hasTrailingPlate: boolean = false
): string => {
  const resolvedIntent = (INTENT_COLORS[intent as ButtonIntent] ? intent : 'primary') as ButtonIntent;
  const usePlateRecipe = hasTrailingPlate && type !== 'icon';
  const padding = type === 'icon'
    ? PADDING_ICON[size]
    : usePlateRecipe
      ? PADDING_WITH_TRAILING_PLATE[size]
      : PADDING[size];
  const sizeStyles = usePlateRecipe
    ? SIZE_STYLES_WITH_TRAILING_PLATE[size]
    : SIZE_STYLES[size];
  const applyDashed = isDashed && canDash(type);

  return [
    'relative inline-flex items-center justify-center font-heading',
    'transition-all duration-200 select-none box-border',
    HIT_TARGET_MIN,
    STATE_LAYER,
    INTERACTION_BASE,
    sizeStyles,
    padding,
    ROUNDED[type],
    applyDashed ? BUTTON_STROKE_DASHED : BUTTON_STROKE,
    INTENT_COLORS[resolvedIntent][type],
    applyDashed ? buttonDashInk[resolvedIntent] : '',
    isSelected ? SELECTED_SURFACE[resolvedIntent][type] : '',
    isDisabled ? '' : 'cursor-pointer',
    className,
  ]
    .filter(Boolean)
    .join(' ');
};
